<script>
const ORIENTATION_OPTIONS = [
  { value: "radial_horizontal", label: "Радиальное, горизонт." },
  { value: "radial_vertical", label: "Радиальное, вертик." },
  { value: "radial_45deg", label: "Радиальное, 45° в зоне нагрузки" },
  { value: "axial", label: "Осевое" },
]

const INSTALLATION_OPTIONS = [
  { value: "screw_direct", label: "Винтовое с пальцем резьбы" },
  { value: "screw_adapter", label: "Винтовое через переходники" },
  { value: "adhesive", label: "Приклеивание" },
  { value: "magnet", label: "Постоянный магнит" },
]

const TREND_PREFIXES = ["RMS(a)", "RMS(v)", "DKW(a)", "Peak(a)", "Crest Factor"]

export default {
  name: "TrendNamesEdit",
  props: {
    agentId: [Number, String],
    channels: {
      type: Array,
      default: () => [],
    },
    isActive: Boolean,
  },
  data() {
    return {
      isPublicLoading: false,
      isAdminLoading: false,
      isSaving: false,
      publicTrendNames: [],
      adminRows: [],
    }
  },
  computed: {
    orientationOptions() {
      return ORIENTATION_OPTIONS
    },
    installationOptions() {
      return INSTALLATION_OPTIONS
    },
    publicTableRows() {
      const channelList = this.channels.length
        ? this.channels
        : this.publicTrendNames.map(item => item.channel)

      return channelList.map((channel, index) => {
        const channelKey = channel || `VIB${index + 1}`
        const item = this.publicTrendNames.find(row => row.channel === channelKey) || {}
        return {
          channel: channelKey,
          name: item.name || "—",
          bearing_id: item.bearing_id ?? "—",
          orientation: item.orientation || "",
          installation_method: item.installation_method || "",
        }
      })
    },
  },
  watch: {
    isActive(newValue) {
      if (newValue) this.loadData()
    },
  },
  methods: {
    optionLabel(options, value) {
      return options.find(item => item.value === value)?.label || value || "—"
    },
    extractChannel(name) {
      const match = (name || "").match(/VIB(\d+)/i)
      return match ? `VIB${match[1]}` : null
    },
    extractDisplayName(name) {
      let result = (name || "").trim()
      for (const prefix of TREND_PREFIXES) {
        const pattern = new RegExp(`^${prefix.replace(/[()]/g, "\\$&")}\\s+`, "i")
        result = result.replace(pattern, "")
      }
      return result.replace(/VIB\d+\s*/i, "").trim()
    },
    async loadData() {
      await this.fetchPublicTrendNames()
      await this.fetchAdminTrendNames()
    },
    syncPublicField(channel, field, value) {
      let item = this.publicTrendNames.find(row => row.channel === channel)
      if (!item) {
        item = {
          channel,
          name: "",
          bearing_id: null,
          orientation: "",
          installation_method: "",
        }
        this.publicTrendNames.push(item)
      }
      this.$set(item, field, value)
    },
    async fetchPublicTrendNames() {
      this.isPublicLoading = true
      try {
        const { data } = await this.$axios.get("public/trend-names", {
          params: { agent_id: this.agentId },
        })
        this.publicTrendNames = data?.trend_names || []
      } catch (e) {
        throw new Error(e)
      } finally {
        this.isPublicLoading = false
      }
    },
    async fetchAdminTrendNames() {
      this.isAdminLoading = true
      try {
        const { data } = await this.$axios.get("admin/trend-names", {
          params: { agent_id: this.agentId },
        })
        this.publicTrendNames = data?.trend_names || this.publicTrendNames
        this.adminRows = (data?.total_trend_names || [])
          .filter(item => !item.deleted_at)
          .map(item => ({
            ...item,
            orientation: item.orientation || "",
            installation_method: item.installation_method || "",
          }))
      } catch (e) {
        throw new Error(e)
      } finally {
        this.isAdminLoading = false
      }
    },
    buildSavePayload() {
      return this.publicTableRows.map(row => ({
        channel: row.channel,
        name: row.name === "—" ? "" : row.name,
        bearing_id: row.bearing_id === "—" ? null : row.bearing_id,
        orientation: row.orientation || null,
        installation_method: row.installation_method || null,
      }))
    },
    async saveAdminTrendNames() {
      const trendNames = this.buildSavePayload()
      if (!trendNames.length) return
      this.isSaving = true
      try {
        await this.$axios.post("admin/trend-names", {
          agent_id: this.agentId,
          trend_names: trendNames,
        })
        this.$buefy.toast.open({ message: "Сохранено", type: "is-success" })
        await this.loadData()
      } catch (e) {
        throw new Error(e)
      } finally {
        this.isSaving = false
      }
    },
    async deleteTrendName(row) {
      if (!row?.id) return
      if (!window.confirm("Удалить запись?")) return
      try {
        await this.$axios.delete(`admin/trend-names/${row.id}`)
        this.$buefy.toast.open({ message: "Удалено", type: "is-success" })
        await this.loadData()
      } catch (e) {
        throw new Error(e)
      }
    },
  },
}
</script>

<template>
  <div class="has-background-white p-5 is-relative" style="min-height: 100px">
    <b-loading v-model="isPublicLoading" :is-full-page="false" />

    <div class="is-flex is-justify-content-space-between is-align-items-center mb-4">
      <h2 class="is-size-5 has-text-weight-semibold">Сгруппированные</h2>
      <b-button
        type="is-info"
        size="is-small"
        :loading="isSaving"
        :disabled="isPublicLoading || !publicTableRows.length"
        @click="saveAdminTrendNames"
      >
        Сохранить
      </b-button>
    </div>
    <b-table
      :data="publicTableRows"
      :loading="isPublicLoading"
      hoverable
      striped
      narrowed
      class="mb-6"
    >
      <b-table-column field="channel" label="Канал" v-slot="props">
        {{ props.row.channel }}
      </b-table-column>
      <b-table-column field="name" label="Название" v-slot="props">
        {{ props.row.name }}
      </b-table-column>
      <!-- <b-table-column field="bearing_id" label="Bearing ID" numeric v-slot="props">
        {{ props.row.bearing_id }}
      </b-table-column> -->
      <b-table-column field="orientation" label="Ориентация" v-slot="props">
        <b-select
          :value="props.row.orientation"
          expanded
          size="is-small"
          @input="syncPublicField(props.row.channel, 'orientation', $event)"
        >
          <option value="">—</option>
          <option
            v-for="option in orientationOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </b-select>
      </b-table-column>
      <b-table-column field="installation_method" label="Способ установки" v-slot="props">
        <b-select
          :value="props.row.installation_method"
          expanded
          size="is-small"
          @input="syncPublicField(props.row.channel, 'installation_method', $event)"
        >
          <option value="">—</option>
          <option
            v-for="option in installationOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </b-select>
      </b-table-column>
    </b-table>

    <h2 class="is-size-5 has-text-weight-semibold mb-4">Все</h2>

    <b-table
      :data="adminRows"
      :loading="isAdminLoading"
      hoverable
      striped
      narrowed
    >
      <b-table-column field="id" label="ID" width="60" numeric v-slot="props">
        {{ props.row.id }}
      </b-table-column>
      <b-table-column field="name" label="Название" v-slot="props">
        {{ props.row.name }}
      </b-table-column>
      <b-table-column field="orientation" label="Ориентация" v-slot="props">
        {{ optionLabel(orientationOptions, props.row.orientation) }}
      </b-table-column>
      <b-table-column field="installation_method" label="Способ установки" v-slot="props">
        {{ optionLabel(installationOptions, props.row.installation_method) }}
      </b-table-column>
      <b-table-column label="Action" width="80" centered v-slot="props">
        <b-button
          type="is-danger is-light"
          size="is-small"
          icon-right="trash"
          @click="deleteTrendName(props.row)"
        />
      </b-table-column>
    </b-table>
  </div>
</template>
